import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ClientDocumentsPanel from "../components/ClientDocumentsPanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "../components/ui/card";
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from "../components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  ArrowLeft, Loader2, Save, User, Mail, Phone, MapPin, FileText, Receipt,
  Monitor, Briefcase, Construction, CreditCard, Clock, DollarSign,
  GraduationCap, CheckCircle2, ExternalLink,
} from "lucide-react";
import { Progress } from "../components/ui/progress";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientDetail() {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [client, setClient] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [trainingAssignments, setTrainingAssignments] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [clientRes, quotesRes, invoicesRes, trainingRes] = await Promise.all([
        axios.get(`${API}/clients/${clientId}`, { headers }),
        axios.get(`${API}/quotes?client_id=${clientId}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/invoices?client_id=${clientId}`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/training/assignments?client_id=${clientId}`, { headers }).catch(() => ({ data: { assignments: [] } })),
      ]);
      setClient(clientRes.data);
      setQuotes(Array.isArray(quotesRes.data) ? quotesRes.data : []);
      setInvoices(Array.isArray(invoicesRes.data) ? invoicesRes.data : []);
      setTrainingAssignments(trainingRes.data?.assignments || []);
    } catch {
      toast.error("Failed to load client data");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/clients/${clientId}`, client, { headers });
      toast.success("Client updated");
    } catch {
      toast.error("Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setClient(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground">Client not found</p>
        <Button variant="link" onClick={() => navigate("/clients")}>Back to Clients</Button>
      </div>
    );
  }

  const totalQuoteValue = quotes.reduce((sum, q) => sum + (q.total_amount || 0), 0);
  const totalInvoiceValue = invoices.reduce((sum, i) => sum + (i.total_amount || 0), 0);
  const paidInvoices = invoices.filter(i => i.status === "paid");

  return (
    <div className="space-y-6" data-testid="client-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/clients")} data-testid="back-to-clients">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">
            {client.title ? `${client.title} ` : ""}{client.first_name} {client.last_name}
          </h1>
          <p className="text-muted-foreground">
            {client.company_name && <span>{client.company_name} &middot; </span>}
            {client.email}
          </p>
        </div>
        <Badge variant={client.status === "active" ? "default" : "secondary"} className="capitalize">
          {client.status}
        </Badge>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-blue-500/10"><FileText className="h-5 w-5 text-blue-600" /></div>
            <div>
              <p className="text-sm text-muted-foreground">Quotes</p>
              <p className="text-xl font-bold">{quotes.length}</p>
              <p className="text-xs text-muted-foreground">${totalQuoteValue.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-500/10"><Receipt className="h-5 w-5 text-emerald-600" /></div>
            <div>
              <p className="text-sm text-muted-foreground">Invoices</p>
              <p className="text-xl font-bold">{invoices.length}</p>
              <p className="text-xs text-muted-foreground">${totalInvoiceValue.toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-500/10"><DollarSign className="h-5 w-5 text-purple-600" /></div>
            <div>
              <p className="text-sm text-muted-foreground">Paid</p>
              <p className="text-xl font-bold">{paidInvoices.length}</p>
              <p className="text-xs text-muted-foreground">${paidInvoices.reduce((s, i) => s + (i.total_amount || 0), 0).toLocaleString()}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-500/10"><GraduationCap className="h-5 w-5 text-amber-600" /></div>
            <div>
              <p className="text-sm text-muted-foreground">Training</p>
              <p className="text-xl font-bold">{trainingAssignments.length}</p>
              <p className="text-xs text-muted-foreground">{trainingAssignments.filter(a => a.status === "completed").length} completed</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="w-full flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="profile" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <User className="h-4 w-4 hidden sm:block" />
            Profile
          </TabsTrigger>
          <TabsTrigger value="portal" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <Monitor className="h-4 w-4 hidden sm:block" />
            Portal
          </TabsTrigger>
          <TabsTrigger value="quotes" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <FileText className="h-4 w-4 hidden sm:block" />
            Quotes
          </TabsTrigger>
          <TabsTrigger value="invoices" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <Receipt className="h-4 w-4 hidden sm:block" />
            Invoices
          </TabsTrigger>
          <TabsTrigger value="training" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <GraduationCap className="h-4 w-4 hidden sm:block" />
            Training
          </TabsTrigger>
          <TabsTrigger value="billing" className="flex-1 min-w-[80px] flex items-center justify-center gap-1">
            <CreditCard className="h-4 w-4 hidden sm:block" />
            Billing
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex-1 min-w-[80px] flex items-center justify-center gap-1" data-testid="client-tab-documents-trigger">
            <FileText className="h-4 w-4 hidden sm:block" />
            Documents
          </TabsTrigger>
        </TabsList>

        {/* ─── Profile Tab ─── */}
        <TabsContent value="profile" className="space-y-4" data-testid="client-tab-profile">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Title</Label>
                <Select value={client.title || ""} onValueChange={(v) => updateField("title", v)}>
                  <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Mr.">Mr.</SelectItem>
                    <SelectItem value="Mrs.">Mrs.</SelectItem>
                    <SelectItem value="Ms.">Ms.</SelectItem>
                    <SelectItem value="Dr.">Dr.</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Preferred Name</Label>
                <Input value={client.preferred_name || ""} onChange={(e) => updateField("preferred_name", e.target.value)} />
              </div>
              <div>
                <Label>First Name *</Label>
                <Input value={client.first_name || ""} onChange={(e) => updateField("first_name", e.target.value)} data-testid="client-first-name" />
              </div>
              <div>
                <Label>Last Name *</Label>
                <Input value={client.last_name || ""} onChange={(e) => updateField("last_name", e.target.value)} data-testid="client-last-name" />
              </div>
              <div>
                <Label>Email</Label>
                <Input type="email" value={client.email || ""} onChange={(e) => updateField("email", e.target.value)} />
              </div>
              <div>
                <Label>Cell Phone</Label>
                <Input value={client.phone_cell || ""} onChange={(e) => updateField("phone_cell", e.target.value)} />
              </div>
              <div>
                <Label>Work Phone</Label>
                <Input value={client.phone_work || ""} onChange={(e) => updateField("phone_work", e.target.value)} />
              </div>
              <div>
                <Label>Status</Label>
                <Select value={client.status || "active"} onValueChange={(v) => updateField("status", v)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="prospect">Prospect</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="md:col-span-2">
                <Label>Notes</Label>
                <Textarea value={client.notes || ""} onChange={(e) => updateField("notes", e.target.value)} rows={3} />
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving} data-testid="save-client-btn">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>
        </TabsContent>

        {/* ─── Portal Management Tab ─── */}
        <TabsContent value="portal" className="space-y-4" data-testid="client-tab-portal">
          <Card>
            <CardContent className="py-12 text-center">
              <div className="mx-auto mb-4 p-5 rounded-full bg-amber-500/10 w-fit">
                <Construction className="h-10 w-10 text-amber-500" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Client Portal Management</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Configure portal access, login credentials, and self-service options for {client.first_name} {client.last_name}. Coming soon!
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Quotes Tab ─── */}
        <TabsContent value="quotes" className="space-y-4" data-testid="client-tab-quotes">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Quotes for {client.first_name}
              </CardTitle>
              <CardDescription>{quotes.length} total quotes &middot; ${totalQuoteValue.toLocaleString()}</CardDescription>
            </CardHeader>
            <CardContent>
              {quotes.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <FileText className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>No quotes for this client yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Quote #</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quotes.map((q) => (
                      <TableRow
                        key={q.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => navigate(`/quotes/${q.id}`)}
                        data-testid={`client-quote-${q.id}`}
                      >
                        <TableCell className="font-medium">{q.quote_number}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{q.property_address || q.description || "-"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">{q.status}</Badge>
                        </TableCell>
                        <TableCell>{q.created_at ? new Date(q.created_at).toLocaleDateString() : "-"}</TableCell>
                        <TableCell className="text-right font-medium">${(q.total_amount || 0).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Invoices Tab ─── */}
        <TabsContent value="invoices" className="space-y-4" data-testid="client-tab-invoices">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Invoices for {client.first_name}
              </CardTitle>
              <CardDescription>{invoices.length} total invoices &middot; ${totalInvoiceValue.toLocaleString()}</CardDescription>
            </CardHeader>
            <CardContent>
              {invoices.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Receipt className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>No invoices for this client yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice #</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.map((inv) => (
                      <TableRow key={inv.id} data-testid={`client-invoice-${inv.id}`}>
                        <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{inv.description || "-"}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className={
                            inv.status === "paid" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" :
                            inv.status === "overdue" ? "bg-red-500/10 text-red-600 border-red-500/30" :
                            ""
                          }>
                            {inv.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{inv.created_at ? new Date(inv.created_at).toLocaleDateString() : "-"}</TableCell>
                        <TableCell className="text-right font-medium">${(inv.total_amount || 0).toLocaleString()}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Training Tab ─── */}
        <TabsContent value="training" className="space-y-4" data-testid="client-tab-training">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GraduationCap className="h-5 w-5" />
                Training Assignments
              </CardTitle>
              <CardDescription>Training programs assigned to {client.first_name}</CardDescription>
            </CardHeader>
            <CardContent>
              {trainingAssignments.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <GraduationCap className="h-10 w-10 mx-auto mb-2 opacity-40" />
                  <p>No training assigned yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {trainingAssignments.map((a) => {
                    const pct = a.total_materials > 0 ? Math.round((a.completed_materials / a.total_materials) * 100) : 0;
                    return (
                      <div key={a.id} className="flex items-center gap-4 p-4 rounded-lg border" data-testid={`client-training-${a.id}`}>
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium">{a.program_title}</h4>
                          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                            <Badge variant="outline" className={
                              a.status === "completed" ? "bg-emerald-500/10 text-emerald-600 border-emerald-500/30" :
                              a.status === "in_progress" ? "bg-blue-500/10 text-blue-600 border-blue-500/30" :
                              a.status === "overdue" ? "bg-red-500/10 text-red-600 border-red-500/30" :
                              ""
                            }>
                              {a.status?.replace("_", " ")}
                            </Badge>
                            {a.due_date && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Due: {a.due_date}</span>}
                          </div>
                        </div>
                        <div className="w-32 flex items-center gap-2">
                          <Progress value={pct} className="flex-1" />
                          <span className="text-xs font-medium w-8 text-right">{pct}%</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Billing Tab ─── */}
        <TabsContent value="billing" className="space-y-4" data-testid="client-tab-billing">
          <Card>
            <CardHeader>
              <CardTitle>Billing Settings</CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>Payment Terms</Label>
                <Select value={client.payment_terms || ""} onValueChange={(v) => updateField("payment_terms", v)}>
                  <SelectTrigger><SelectValue placeholder="Select terms" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="net_15">Net 15</SelectItem>
                    <SelectItem value="net_30">Net 30</SelectItem>
                    <SelectItem value="net_45">Net 45</SelectItem>
                    <SelectItem value="net_60">Net 60</SelectItem>
                    <SelectItem value="due_on_receipt">Due on Receipt</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Credit Limit</Label>
                <Input
                  type="number"
                  value={client.credit_limit || ""}
                  onChange={(e) => updateField("credit_limit", e.target.value ? parseFloat(e.target.value) : null)}
                  placeholder="0.00"
                />
              </div>
              <div className="flex items-center gap-2 md:col-span-2">
                <input
                  type="checkbox"
                  checked={client.tax_exempt || false}
                  onChange={(e) => updateField("tax_exempt", e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300"
                />
                <Label>Tax Exempt</Label>
              </div>
            </CardContent>
          </Card>
          <div className="flex justify-end">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Billing Settings
            </Button>
          </div>
        </TabsContent>

        {/* ─── Documents Tab (Phase B.5 — Saved attachments via Send & File) ─── */}
        <TabsContent value="documents" className="space-y-4" data-testid="client-tab-documents">
          <ClientDocumentsPanel clientId={clientId} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
